import { Col, Row } from 'react-bootstrap'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { useCallback, useMemo } from 'react'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { FormFieldDefinition } from '../../../hooks'

export interface PredefinedContactAdminSettingsProps {
  serviceId: IntegrationServiceId
  disabled?: boolean
}
const crmEnableCustomFieldsList = [IntegrationServiceId.SALESFORCE, IntegrationServiceId.PRACTIFI, IntegrationServiceId.XLR8, IntegrationServiceId.ZOHO]

const PREDEFINED_CUSTOMIZABLE_FIELDS = [{ zocksKey: 'email', label: 'Email', required: true }]

export const PredefinedContactAdminSettings: React.FC<PredefinedContactAdminSettingsProps> = ({ serviceId, disabled }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const contactFields = watch('mappings.contact.fields')
  const currentObjectName = watch('mappings.contact.objectName')

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: currentObjectName! },
    },
    { skip: !currentObjectName || !accountId || disabled },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const fieldOptions: SelectOption[] = useMemo(
    () =>
      currentObjectFormFieldDefinitions.map((field: { name: string; label: string }): SelectOption => {
        return {
          id: field.name,
          label: `${field.label} (${field.name})`,
        }
      }) || [],
    [currentObjectFormFieldDefinitions],
  )

  const handleFieldChange = useCallback(
    (zocksKey: string, externalField: string) => {
      const newDescriptors = {
        type: 'field' as const,
        value: externalField,
      }

      setValue(`mappings.contact.fields.${zocksKey}`, [newDescriptors], {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  return (
    <>
      {crmEnableCustomFieldsList.includes(serviceId) &&
        PREDEFINED_CUSTOMIZABLE_FIELDS.map((mapping) => {
          const storedValue = contactFields?.[mapping.zocksKey]?.[0]?.value

          const currentOption =
            fieldOptions.find((opt: SelectOption) => opt.id === storedValue) || (storedValue ? { id: storedValue, label: storedValue } : null)

          const fieldError = errors.mappings?.contact?.fields?.[mapping.zocksKey]
          const label = mapping.label

          return (
            <Row key={mapping.zocksKey} className='mb-2 mt-2 align-items-center'>
              <Col>
                <div className='mb-1'>
                  <span className='zocks-text-gray-600'>{label}</span>
                  {mapping.required && <span className='text-danger ms-1'>*</span>}
                </div>

                <SelectDropdown
                  searchable
                  options={fieldOptions}
                  onOptionSelected={(selected) => handleFieldChange(mapping.zocksKey, selected.id)}
                  placeholder='Select field'
                  initialSelectedOption={currentOption}
                  loading={isDescribeFetching}
                  disabled={disabled}
                />

                <div className='d-block' style={{ minHeight: '20px' }}>
                  {fieldError && <div className='invalid-feedback d-block'>{fieldError.message || 'This field is required'}</div>}
                </div>
              </Col>
            </Row>
          )
        })}
    </>
  )
}
